// Pedigree config (validatePedigreeConfig output) → TokenisedPedigree: a clean
// internal model for the detectors. Pure; no I/O, no React.
//
// Input members carry: pid, gender, fatherId, motherId, spouseIds[],
// proBandId, dob/dod, diagnoses[], genotypes[], phenotypes[]. childrenIds /
// siblingIds are NOT populated by validatePedigreeConfig, so we derive them
// from fatherId/motherId here. "Affected by disease D" = a diagnosis with
// id === D and displayOnPedigree === true. When conditionCarrierEnabled is true
// (FEATURE_CARRIER_STATUS_IN_CONDITION) the per-condition affectedStatus overlay
// is honoured: a displayed condition whose explicit affectedStatus is set but is
// not 'affected' (clinician downgrade to carrier/unaffected/unknown) is excluded
// from the analysis affected-set, keeping it aligned with the rendered
// symbol/legend. Flag off → affectedStatus is never consulted (byte-identical).

import { toSex } from './constants';
import { structureHash } from './structureHash';
import { getPersonAge } from 'src/util/util';
import {
  BUCKET,
  hasClassificationConflict,
  leadingClassification
} from 'src/util/variantPathogenicity';

// Classification handling is delegated to the shared module so this file no
// longer holds its own copy. It previously kept a third, subtly different
// rule — it conflated the clinician's classification with the laboratory's
// (reading lab FIRST, so a clinician's correction could neither lead nor
// silence an alert), and flagged a conflict only for pathogenic-vs-benign,
// never for pathogenic-vs-uncertain. A record could therefore warn in the
// sidebar and not in the analysis, or the reverse.
function normaliseGenotype(g) {
  const gene = String((g && (g.symbol || g.gene || g.name)) || '')
    .split(' (')[0]
    .trim();
  const variants = Array.isArray(g && g.variants) ? g.variants : [];

  // The engine needs one answer per gene for its molecular-evidence rules, so
  // an aggregate is unavoidable here. It is taken from each variant's LEADING
  // source (clinician → laboratory → ClinVar, the documented precedence), and
  // the strongest across variants wins — a pathogenic variant is molecular
  // evidence regardless of what else the gene carries.
  let strongest = 'unknown';
  let clinvarConflict = false;
  // The same aggregate, but computed ONLY over variants that could be
  // inherited. A somatic variant is acquired, so it is not evidence of a
  // transmitted Mendelian cause — reading it as such is how a somatic JAK2
  // driver on a myelofibrosis patient produced "autosomal dominant, vertical
  // transmission". Note the gene-disease association cannot separate these:
  // JAK2 genuinely IS associated with primary myelofibrosis. Only the origin
  // does.
  //
  // `unknown` origin counts as potentially heritable. Almost every existing
  // record predates the field, so excluding unknowns would silently withdraw
  // molecular evidence from every pedigree in the system; and the ordinary
  // case for a recorded variant on a family record IS germline. Somatic has
  // to be stated to be acted on, which is the direction that fails toward
  // today's behaviour rather than away from it.
  let germlineStrongest = 'unknown';
  let hasSomaticPathogenic = false;

  const rank = (bucket, current) => {
    if (bucket === BUCKET.pathogenic) return 'pathogenic';
    if (bucket === BUCKET.uncertain && current === 'unknown') return 'vus';
    if (bucket === BUCKET.benign && current === 'unknown') return 'benign';
    return current;
  };

  for (const v of variants) {
    if (hasClassificationConflict(v)) clinvarConflict = true;

    const bucket = leadingClassification(v)?.bucket || null;
    strongest = rank(bucket, strongest);

    const somatic = String(v?.origin || '').toLowerCase() === 'somatic';
    if (somatic) {
      if (bucket === BUCKET.pathogenic) hasSomaticPathogenic = true;
    } else {
      germlineStrongest = rank(bucket, germlineStrongest);
    }
  }

  return {
    gene,
    carrierStatus: String((g && g.carrierStatus) || '').trim() || null,
    // When the result was recorded. Carried so a surface can SHOW the age of a
    // result it is relying on. Deliberately not used to decide anything: no
    // rule branches on recency, because refusing to honour an undated result
    // would put us back to offering a test to someone with a known pathogenic
    // variant on file — the more dangerous of the two errors.
    dateRecorded: (g && g.dateRecorded) || null,
    // Strongest classification across ALL variants, regardless of origin.
    // Display and alerting use this: a somatic pathogenic finding is still a
    // pathogenic finding and must not vanish from the record.
    interpretation: strongest,
    // Strongest classification across variants that could be INHERITED. This
    // is what an inheritance rule must use.
    germlineInterpretation: germlineStrongest,
    // True when a pathogenic variant on this gene is explicitly somatic, so a
    // detector can explain why it declined to treat it as heritable evidence.
    hasSomaticPathogenic,
    clinvarConflict
  };
}

function isDeceased(m) {
  return Boolean(m.dod) || m.isAlive === 'No' || m.alive === 'No';
}

function tokenLabel(m, indexByPid) {
  if (m.proBandId) return 'PROBAND';
  const rel = (m.relationship || '').trim();
  if (rel) return rel.replace(/\s+/g, '_').toUpperCase();
  return `IND_${indexByPid.get(String(m.pid))}`;
}

// Ancestor set (pids) of an individual, walking parent edges.
function ancestorsOf(pid, byPid) {
  const out = new Set();
  const stack = [pid];
  while (stack.length) {
    const cur = byPid.get(stack.pop());
    if (!cur) continue;
    for (const p of [cur.fatherId, cur.motherId]) {
      if (p && byPid.has(p) && !out.has(p)) {
        out.add(p);
        stack.push(p);
      }
    }
  }
  return out;
}

// A consanguinity loop = a mating pair that shares an ancestor. F is left null
// (a precise inbreeding coefficient is out of scope for v1).
function detectConsanguinity(individuals, byPid) {
  const loops = [];
  const seen = new Set();
  for (const ind of individuals) {
    for (const spouseId of ind.spouseIds) {
      const key = [ind.pid, spouseId].sort().join('|');
      if (seen.has(key)) continue;
      seen.add(key);
      const a = ancestorsOf(ind.pid, byPid);
      const b = ancestorsOf(spouseId, byPid);
      const shared = [...a].some((x) => b.has(x));
      if (shared)
        loops.push({ partners: [ind.pid, spouseId], coefficientF: null });
    }
  }
  return loops;
}

export function tokenisePedigree(
  members = [],
  { carrierStatusByMember = {}, conditionCarrierEnabled = false } = {}
) {
  const nodes = (members || []).filter((m) => m && m.pid != null);

  // Stable index for fallback tokens (sorted by pid string → order-independent).
  const indexByPid = new Map(
    [...nodes]
      .map((m) => String(m.pid))
      .sort()
      .map((pid, i) => [pid, i + 1])
  );

  const familyDiseases = new Map(); // diseaseId -> { id, name }
  const individuals = nodes.map((m) => {
    const affectedDiseases = new Set();
    for (const d of m.diagnoses || []) {
      if (d && d.id && d.displayOnPedigree === true) {
        // Flag-gated downgrade: an explicit affectedStatus that is set but not
        // 'affected' removes the condition from the affected-set (carrier etc.).
        const downgraded =
          conditionCarrierEnabled &&
          d.affectedStatus &&
          d.affectedStatus !== 'affected';
        if (!downgraded) affectedDiseases.add(d.id);
        if (!familyDiseases.has(d.id))
          familyDiseases.set(d.id, { id: d.id, name: d.name });
      }
    }
    return {
      pid: String(m.pid),
      token: tokenLabel(m, indexByPid),
      isProband: Boolean(m.proBandId),
      sex: toSex(m.gender),
      isDummy: Boolean(m.isDummy),
      affectedDiseases,
      affected: affectedDiseases.size > 0,
      deceased: isDeceased(m),
      ageAtAssessment: (() => {
        try {
          const a = getPersonAge(m);
          return Number.isFinite(a) ? a : null;
        } catch (_) {
          return null;
        }
      })(),
      phenotypes: (m.phenotypes || [])
        .filter((p) => p && p.id && p.observed !== false)
        .map((p) => p.id),
      genotypes: (m.genotypes || []).map(normaliseGenotype),
      carrierByDisease: carrierStatusByMember[String(m.pid)] || {},
      fatherId: m.fatherId != null ? String(m.fatherId) : null,
      motherId: m.motherId != null ? String(m.motherId) : null,
      spouseIds: (Array.isArray(m.spouseIds) ? m.spouseIds : []).map(String),
      childrenIds: [],
      siblingIds: []
    };
  });

  const byPid = new Map(individuals.map((i) => [i.pid, i]));

  // Derive children from parent edges.
  for (const ind of individuals) {
    for (const parentId of [ind.fatherId, ind.motherId]) {
      const parent = parentId && byPid.get(parentId);
      if (parent && !parent.childrenIds.includes(ind.pid))
        parent.childrenIds.push(ind.pid);
    }
  }
  // Siblings = share at least one parent (excluding self).
  for (const ind of individuals) {
    const sibs = new Set();
    for (const parentId of [ind.fatherId, ind.motherId]) {
      const parent = parentId && byPid.get(parentId);
      if (!parent) continue;
      for (const childId of parent.childrenIds)
        if (childId !== ind.pid) sibs.add(childId);
    }
    ind.siblingIds = [...sibs];
  }

  const proband =
    individuals.find((i) => i.isProband) || individuals[0] || null;

  return {
    proband: proband ? proband.pid : null,
    individuals,
    byPid,
    familyDiseases: [...familyDiseases.values()],
    consanguinityLoops: detectConsanguinity(individuals, byPid),
    structureHash: structureHash(members)
  };
}
